import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage, mobileNumberInputSchema } from '@packages/shared';
import { useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

const returningUserSchema = z.object({
  mobileNumber: mobileNumberInputSchema,
});

export const SiteOccupiedForm = ({
  companyLocationCode,
  siteCode,
  maskedMobileNumber,
}: {
  companyLocationCode: string;
  siteCode: string;
  maskedMobileNumber: string | null;
}) => {
  const convex = useConvex();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(returningUserSchema),
    defaultValues: { mobileNumber: '' },
  });

  const onValidSubmit = async (values: z.output<typeof returningUserSchema>) => {
    setIsSubmitting(true);
    try {
      await convex.mutation(api.sessions.resumeSession, {
        mobileNumber: values.mobileNumber,
        companyLocationCode,
        siteCode,
      });
      await navigate({
        to: '/$companyLocationCode/$siteCode/sms-sent',
        params: { companyLocationCode, siteCode },
        search: { mobile: values.mobileNumber },
      });
    } catch (error) {
      toast.error(getUserErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">This site is occupied</h2>
        <p className="text-sm">
          {maskedMobileNumber
            ? `Occupied by mobile ${maskedMobileNumber}. Enter your mobile number to continue.`
            : 'Enter your mobile number to continue.'}
        </p>
      </div>
      <FormWrapper {...form}>
        <form
          onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
          className="grid gap-4"
        >
          <FormField
            control={form.control}
            name="mobileNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your mobile number</FormLabel>
                <FormControl>
                  <Input size="lg" type="tel" {...field} placeholder={maskedMobileNumber ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <ActionButton type="submit" className="w-full" size="lg" loading={isSubmitting}>
            Continue
          </ActionButton>
        </form>
      </FormWrapper>
    </div>
  );
};
